import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { prepareDefaultHeaders } from '../../api'
import { BlueprintFamilyExampleImage } from '../../api/blueprintFamilies/exampleImages'
import { TranslationMap } from '../../types/TranslationMap'
import { acceptedEventTypesBody, exampleImagesBody } from './archetypeWritePayload'

// An event type the market archetype accepts. categoryId is the "event" tag group the type belongs
// to, and is absent for types without one. count is how many *active* events of that type the
// archetype has, so an accepted type nothing uses yet reports 0.
export interface MarketArchetypeEventType {
  typeId: string
  categoryId?: string
  count: number
}

// An active event of the archetype's game blueprints whose type the archetype does not accept.
export interface MarketArchetypeUnacceptedEvent {
  eventId: string
  name: string
  gameId: string
  // The game-level blueprint the event is linked to.
  blueprintId: string
  // The event's typeAccepted: an analyst marked it as a legitimate exception. It is listed either way.
  accepted: boolean
}

// An event type the archetype's active events use without the archetype accepting it, with those
// events. Never empty: a type is only reported here while an active event uses it.
export interface MarketArchetypeUnacceptedEventType {
  typeId: string
  categoryId?: string
  events: MarketArchetypeUnacceptedEvent[]
}

// Notice that in UI we call this a "Market Level Blueprint"
// Market Level Blueprints are global across all LiveOps tracked games
export interface MarketArchetype {
  id: string
  names: TranslationMap
  descriptions: TranslationMap
  discontinued: boolean
  // Ordered list of example screenshots surfaced on the Data Glossary card gallery. Each one is
  // picked off a tracked live event rather than uploaded, so it carries the event it came from.
  // Only `url` and `eventId` are stored; the rest is resolved by the BE and ignored on write.
  glossaryImages?: BlueprintFamilyExampleImage[]
  createdAt?: number
  modifiedAt?: number
  // The event types this archetype accepts. There is no allowedEventTypeIds on the read side; that
  // field exists on writes only, and these are what belong in it. Ordered by count descending and
  // then by typeId, so accepted types nothing uses yet come last. An empty list means nothing has
  // been declared for the archetype yet.
  eventTypes?: MarketArchetypeEventType[]
  // The types its active events use that it does not accept, events included, in the same order.
  // Never send these back in allowedEventTypeIds unless the analyst chose to accept them.
  unacceptedEventTypes?: MarketArchetypeUnacceptedEventType[]
}

// The write side takes at most this many accepted event type ids.
export const MAX_ACCEPTED_EVENT_TYPES = 10

export interface CreateArchetypeRequest {
  names: TranslationMap
  descriptions: TranslationMap
  discontinued: boolean
  glossaryImages?: BlueprintFamilyExampleImage[]
  // Write-only: the response reports the accepted types through eventTypes instead. At most
  // MAX_ACCEPTED_EVENT_TYPES ids.
  allowedEventTypeIds?: string[]
}

export const internalMarketsApi = createApi({
  reducerPath: 'internalMarketsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: window.GR_API_URLS.API_URL_CORE + '/i/',
    prepareHeaders: prepareDefaultHeaders,
  }),
  tagTypes: ['trackedArchetypes'],
  endpoints: (builder) => ({
    getTrackedArchetypes: builder.query<MarketArchetype[], void>({
      query: () => 'market/tracked/archetypes',
      providesTags: ['trackedArchetypes'],
    }),
    getTrackedArchetype: builder.query<MarketArchetype, string>({
      query: (archetypeId) => `market/tracked/archetypes/${archetypeId}`,
      providesTags: (result, error, archetypeId) => [{ type: 'trackedArchetypes', id: archetypeId }],
    }),
    createMarketArchetype: builder.mutation<MarketArchetype, CreateArchetypeRequest>({
      query: ({ glossaryImages, allowedEventTypeIds, ...body }) => ({
        url: 'market/tracked/archetypes',
        method: 'POST',
        body: { ...body, ...exampleImagesBody(glossaryImages), ...acceptedEventTypesBody(allowedEventTypeIds) },
      }),
      invalidatesTags: ['trackedArchetypes'],
    }),
    updateMarketArchetype: builder.mutation<
      MarketArchetype,
      {
        archetypeId: string
        names: TranslationMap
        descriptions: TranslationMap
        discontinued: boolean
        glossaryImages?: BlueprintFamilyExampleImage[]
        allowedEventTypeIds?: string[]
      }
    >({
      query: ({ archetypeId, names, descriptions, discontinued, glossaryImages, allowedEventTypeIds }) => ({
        url: `market/tracked/archetypes/${archetypeId}`,
        method: 'PUT',
        body: { names, descriptions, discontinued, ...exampleImagesBody(glossaryImages), ...acceptedEventTypesBody(allowedEventTypeIds) },
      }),
      invalidatesTags: ['trackedArchetypes'],
    }),
    deleteMarketArchetype: builder.mutation<void, string>({
      query: (archetypeId) => ({
        url: `market/tracked/archetypes/${archetypeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['trackedArchetypes'],
    }),
  }),
})

export const {
  useGetTrackedArchetypesQuery,
  useGetTrackedArchetypeQuery,
  useCreateMarketArchetypeMutation,
  useUpdateMarketArchetypeMutation,
  useDeleteMarketArchetypeMutation,
} = internalMarketsApi
